import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader } from "@/components/ui/card";

/** 4 KPI cards skeleton (used in Operations Overview) */
export function KPICardsSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className={`grid grid-cols-2 ${count === 4 ? "lg:grid-cols-4" : count === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"} gap-3`}>
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i} className="border-border/60">
          <CardContent className="p-4 space-y-2">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-7 w-24" />
            <Skeleton className="h-3 w-20" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/** Card with title + rows skeleton (permits, checkins, etc.) */
export function CardListSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-5 rounded" />
          <Skeleton className="h-5 w-40" />
        </div>
        <Skeleton className="h-8 w-24 rounded-md" />
      </CardHeader>
      <CardContent className="space-y-3">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center justify-between p-3 rounded-lg border border-border/50">
            <div className="space-y-1.5 flex-1">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-48" />
            </div>
            <Skeleton className="h-6 w-12 rounded" />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/** Operations dashboard full skeleton */
export function OperationsDashboardSkeleton() {
  return (
    <div className="space-y-5 p-5 lg:p-6">
      <KPICardsSkeleton />
      <Card className="border-border/60">
        <CardContent className="p-4 space-y-3">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-2 w-full rounded-full" />
        </CardContent>
      </Card>
      <Card className="border-border/60">
        <CardContent className="p-4">
          <Skeleton className="h-4 w-28 mb-3" />
          <Skeleton className="h-44 w-full rounded-lg" />
        </CardContent>
      </Card>
    </div>
  );
}

/** Billing page skeleton */
export function BillingSkeleton() {
  return (
    <div className="container pt-8 max-w-3xl space-y-6">
      <div className="space-y-2">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-96" />
      </div>
      {[1, 2, 3].map(i => (
        <Card key={i} className="rounded-2xl">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-5 rounded" />
              <Skeleton className="h-5 w-32" />
            </div>
            <Skeleton className="h-8 w-24" />
            <Skeleton className="h-2 w-full rounded-full" />
            <Skeleton className="h-3 w-48" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/** Location detail panel skeleton */
export function LocationDetailSkeleton() {
  return (
    <div className="p-4 space-y-4">
      <div className="space-y-2">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-3 w-56" />
        <div className="flex gap-2 mt-2">
          <Skeleton className="h-5 w-16 rounded-full" />
          <Skeleton className="h-5 w-10" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {[1, 2].map(i => (
          <div key={i} className="rounded-lg border border-border p-3 space-y-2">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-6 w-12" />
            <Skeleton className="h-1.5 w-full rounded-full" />
          </div>
        ))}
      </div>
      <div className="space-y-2">
        <Skeleton className="h-3 w-32" />
        <Skeleton className="h-20 w-full rounded-lg" />
      </div>
    </div>
  );
}

/** Survival Score skeleton */
export function SurvivalScoreSkeleton() {
  return (
    <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 mb-8">
      <div className="flex items-center gap-3 mb-6">
        <Skeleton className="h-10 w-10 rounded-xl" />
        <div className="space-y-1.5">
          <Skeleton className="h-5 w-52" />
          <Skeleton className="h-3 w-36" />
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-3">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="space-y-1">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-9 w-full rounded-md" />
            </div>
          ))}
          <Skeleton className="h-9 w-full rounded-md" />
        </div>
        <div className="flex flex-col items-center justify-center">
          <Skeleton className="h-24 w-24 rounded-full" />
          <Skeleton className="h-4 w-16 mt-2" />
        </div>
        <div className="space-y-3">
          <Skeleton className="h-4 w-36" />
          {[1, 2, 3].map(i => (
            <div key={i} className="flex items-start gap-2.5">
              <Skeleton className="h-5 w-5 rounded-full shrink-0" />
              <Skeleton className="h-4 w-full" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Checklist / inspection skeleton */
export function ChecklistSkeleton({ rows = 8 }: { rows?: number }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-5 rounded" />
          <Skeleton className="h-5 w-44" />
          <Skeleton className="h-4 w-20 ml-auto" />
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        <Skeleton className="h-3 w-72 mb-4" />
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-start gap-3 p-2">
            <Skeleton className="h-4 w-4 rounded mt-0.5" />
            <Skeleton className="h-4 flex-1" />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
